import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  useMediaQuery,
  IconButton,
  Stack,
  Fade,
} from "@mui/material";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { useSwipeable } from "react-swipeable";

const SLIDE_DURATION = 10000; // 10 seconds
const TRANSITION_DURATION = 500; // 0.5 seconds

const content = [
  {
    title: "Web Design",
    header: "Elevating Web Design Beyond Expectations",
    desc: "Welcome to a digital oasis where creativity meets functionality. Our passion for design fuels every pixel we craft, delivering captivating websites that leave a lasting impression.",
  },
  {
    title: "GIS Services",
    header: "Welcome to Jeremy Solutions GIS Services",
    desc: "At Jeremy Solutions, we specialize in providing cutting-edge Geographic Information System (GIS) services tailored to meet the unique needs of our clients.",
  },
  {
    title: "Digital Marketing",
    header: "Welcome to Jeremy Solutions Digital Marketing Services",
    desc: "At Jeremy Solutions, we specialize in providing comprehensive digital marketing services that drive results and help businesses thrive in the online world.",
  },
  {
    title: "Art Sales",
    header: "Welcome to Jeremy Solutions Art Sales Services",
    desc: "At Jeremy Solutions, we are passionate about connecting artists with collectors and enthusiasts through our comprehensive art sales services.",
  },
];

export default function Hero({ index = 0, autoPlay = true }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [currentIndex, setCurrentIndex] = useState(index === 5 ? 0 : index);
  const [isVisible, setIsVisible] = useState(true);

  const handleSlideChange = (direction) => {
    setIsVisible(false);
    setTimeout(() => {
      if (direction === "next") {
        setCurrentIndex((prev) => (prev + 1) % content.length);
      } else {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : content.length - 1));
      }
      setIsVisible(true);
    }, TRANSITION_DURATION);
  };

  useEffect(() => {
    if (!autoPlay) return;

    const timer = setInterval(() => {
      handleSlideChange("next");
    }, SLIDE_DURATION);

    return () => clearInterval(timer);
  }, [currentIndex, autoPlay]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSlideChange("next"),
    onSwipedRight: () => handleSlideChange("prev"),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  return (
    <Box
      {...swipeHandlers}
      sx={{
        minHeight: "95vh",
        display: "flex",
        pt: { xs: 10, md: 12 },
        alignItems: "center",
        background: (theme) => `linear-gradient(45deg, 
          ${theme.palette.info.dark} 0%,
          ${theme.palette.primary.dark} 100%
        )`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <Fade in={isVisible} timeout={TRANSITION_DURATION}>
          <Stack
            spacing={4}
            sx={{
              color: "common.white",
              maxWidth: { xs: "100%", md: "60%" },
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "4rem" },
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              {content[currentIndex].title}
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.5rem", md: "2rem" },
                fontWeight: 500,
                color: "primary.light",
              }}
            >
              {content[currentIndex].header}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "1rem", md: "1.125rem" },
                lineHeight: 1.8,
                maxWidth: "90%",
              }}
            >
              {content[currentIndex].desc}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                width: "fit-content",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: 4,
                },
              }}
            >
              Enquire Now
            </Button>
          </Stack>
        </Fade>
      </Container>

      {!isMobile && (
        <Stack
          spacing={2}
          sx={{
            position: "absolute",
            right: { xs: 20, md: 40 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
          }}
        >
          <IconButton
            onClick={() => handleSlideChange("prev")}
            sx={{
              color: "common.white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <KeyboardArrowUp />
          </IconButton>
          <IconButton
            onClick={() => handleSlideChange("next")}
            sx={{
              color: "common.white",
              bgcolor: "rgba(255, 255, 255, 0.1)",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Stack>
      )}
    </Box>
  );
}
